import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { chatChannelService } from "@/services/agent/ChatChannelService";
import { ChatPlatform, OwnSlackAppCredentials } from "@/types/agent/chat-channels";

const chatChannelsKey = (agentId: string | undefined) => ["chat-channels", agentId];
const ownSlackAppKey = (agentId: string | undefined) => ["own-slack-app", agentId];

export const useChatChannels = (agentId: string | undefined) => {
  return useQuery({
    queryKey: chatChannelsKey(agentId),
    queryFn: () => chatChannelService.list(agentId!),
    enabled: !!agentId,
  });
};

export const useChatChannelInstallLink = (agentId: string | undefined, platform: ChatPlatform) => {
  return useQuery({
    queryKey: ["chat-channel-install-link", agentId, platform],
    queryFn: () => chatChannelService.getInstallLink(agentId!, platform),
    enabled: !!agentId,
    staleTime: Infinity,
  });
};

export const useOwnSlackApp = (agentId: string | undefined) => {
  return useQuery({
    queryKey: ownSlackAppKey(agentId),
    queryFn: () => chatChannelService.getOwnApp(agentId!),
    enabled: !!agentId,
  });
};

export const useOwnSlackAppActions = (agentId: string | undefined) => {
  const queryClient = useQueryClient();
  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ownSlackAppKey(agentId) });
    queryClient.invalidateQueries({ queryKey: chatChannelsKey(agentId) });
  };

  return {
    create: useMutation({ mutationFn: () => chatChannelService.createOwnApp(agentId!), onSuccess }),
    saveCredentials: useMutation({
      mutationFn: (credentials: OwnSlackAppCredentials) => chatChannelService.saveOwnAppCredentials(agentId!, credentials),
      onSuccess,
    }),
    markManifestUpdated: useMutation({ mutationFn: () => chatChannelService.markOwnAppManifestUpdated(agentId!), onSuccess }),
    remove: useMutation({ mutationFn: () => chatChannelService.removeOwnApp(agentId!), onSuccess }),
  };
};

export const useSetChatChannelPaused =(agentId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ installId, paused }: { installId: string; paused: boolean }) =>
      chatChannelService.setPaused(agentId!, installId, paused),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatChannelsKey(agentId) });
    },
  });
};

export const useDisconnectChatChannel = (agentId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (installId: string) => chatChannelService.disconnect(agentId!, installId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatChannelsKey(agentId) });
    },
  });
};
